import { combineActions, handleActions } from 'redux-actions';
import {
    DELETE_SAVE_SEARCH_REQUEST,
    DELETE_SAVE_SEARCH_SUCCESS,
    DELETE_SAVED_SEARCH_FAIL,
    DELETE_SAVED_SEARCH_REQUEST,
    DELETE_SAVED_SEARCH_SUCCESS,
    LOAD_SAVED_SEARCH_ITEMS_FAIL,
    LOAD_SAVED_SEARCH_ITEMS_REQUEST,
    LOAD_SAVED_SEARCH_ITEMS_SUCCESS,
    LOAD_SAVED_SEARCHES_FAIL,
    LOAD_SAVED_SEARCHES_REQUEST,
    LOAD_SAVED_SEARCHES_SUCCESS,
    LOG_OUT_BIDDER,
    SAVED_SEARCH_FILTER_CHANGE,
    SAVED_SEARCH_FILTER_CHANGE_FAIL,
    SUBSCRIBE_ALL_SAVED_SEARCH_FAIL,
    SUBSCRIBE_ALL_SAVED_SEARCH_REQUEST,
    SUBSCRIBE_ALL_SAVED_SEARCH_SUCCESS,
    SUBSCRIBE_SAVED_SEARCH_FAIL,
    SUBSCRIBE_SAVED_SEARCH_REQUEST,
    SUBSCRIBE_SAVED_SEARCH_SUCCESS,
    UNSUBSCRIBE_ALL_SAVED_SEARCH_FAIL,
    UNSUBSCRIBE_ALL_SAVED_SEARCH_REQUEST,
    UNSUBSCRIBE_ALL_SAVED_SEARCH_SUCCESS,
    UNSUBSCRIBE_SAVED_SEARCH_FAIL,
    UNSUBSCRIBE_SAVED_SEARCH_REQUEST,
    UNSUBSCRIBE_SAVED_SEARCH_SUCCESS,
} from './actions';
import { ActionWithPayload } from '@/types/redux';
import api from '@/redux/api/saveSearch';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { getUnfollowSearchAnalytics } from './analytics';
import { GlobalState } from '../rootReducer';
import isEmpty from 'lodash/isEmpty';
import ms from 'ms';
import { pageSlicer } from '@/utils/pagination';
import { PaginationFilter } from '@liveauctioneers/caterwaul-components/types/PaginationFilter';
import { savedSearchItemSortOrders } from '@/enums';
import { toFacetString } from './savedSearchFacets';

export const LOADING_SAVED_SEARCH_ITEM_IDS = 'saved-search-items';

const REDUX_STORE_TIME = ms('30m');

/* reducer */
const DEFAULT_STATE = {
    error: undefined,
    itemIds: [],
    itemsById: {},
    itemsLoaded: undefined,
    itemsLoading: false,
    savedSearchesRecentFilters: {
        keyword: '',
        page: 1,
        pageSize: 24,
        sort: savedSearchItemSortOrders.NEWLY_LISTED,
        totalRecords: 0,
    },
    searches: [],
    searchesLoaded: undefined,
    searchesLoading: false,
};

export type SavedSearchState = typeof DEFAULT_STATE & {
    error?: string;
    savedSearchesRecentFilters: PaginationFilter;
};

export const savedSearch = handleActions(
    {
        [combineActions(LOAD_SAVED_SEARCHES_FAIL, LOAD_SAVED_SEARCH_ITEMS_FAIL, SAVED_SEARCH_FILTER_CHANGE_FAIL)]: (
            state: SavedSearchState,
            action: ActionWithPayload<any>
        ) => ({
            ...state,
            error: action.payload,
            itemsLoading: false,
            searchesLoading: false,
        }),
        [DELETE_SAVED_SEARCH_REQUEST]: (state: SavedSearchState, action: ActionWithPayload<any>) => {
            let searches = [];
            cloneDeep(state.searches).forEach((x) => {
                if (x.savedSearchId !== action.payload) {
                    searches.push(x);
                }
            });

            return {
                ...state,
                searches,
            };
        },
        [LOAD_SAVED_SEARCH_ITEMS_REQUEST]: (state: SavedSearchState) => ({
            ...state,
            error: undefined,
            itemsLoaded: undefined,
            itemsLoading: true,
        }),
        [LOAD_SAVED_SEARCH_ITEMS_SUCCESS]: (state: SavedSearchState, action: ActionWithPayload<any>) => {
            let itemsById = {};
            let itemIds = [];
            action.payload?.data?.forEach((x) => {
                itemsById[x.itemId] = {
                    itemId: x.itemId,
                    publishDate: x.publishDate,
                    //todo don't have both date and ts
                    publishTs: Date.parse(x.publishDate) / 1000,
                };
                itemIds.push(x.itemId);
            });
            return {
                ...state,
                itemIds,
                itemsById,
                itemsLoaded: new Date(),
                itemsLoading: false,
                savedSearchesRecentFilters: {
                    ...state.savedSearchesRecentFilters,
                    totalRecords: action.payload.totalRecords,
                },
            };
        },
        [LOAD_SAVED_SEARCHES_REQUEST]: (state: SavedSearchState) => ({
            ...state,
            searchesLoading: true,
        }),
        [LOAD_SAVED_SEARCHES_SUCCESS]: (state: SavedSearchState, action: ActionWithPayload<any>) => ({
            ...state,
            searches: action.payload,
            searchesLoaded: new Date(),
            searchesLoading: false,
        }),
        [LOG_OUT_BIDDER]: () => ({
            ...DEFAULT_STATE,
        }),
        [SAVED_SEARCH_FILTER_CHANGE]: (state: SavedSearchState, action: ActionWithPayload<PaginationFilter>) => {
            let newState = {
                ...state,
                itemIds: DEFAULT_STATE.itemIds,
                itemsById: DEFAULT_STATE.itemsById,
                itemsLoaded: DEFAULT_STATE.itemsLoaded,
                savedSearchesRecentFilters: {
                    ...state.savedSearchesRecentFilters,
                    ...action.payload,
                },
            };
            return newState;
        },
        [SUBSCRIBE_ALL_SAVED_SEARCH_REQUEST]: (state: SavedSearchState) => {
            let searches = cloneDeep(state.searches);
            searches.forEach((x) => {
                x.sendAlert = true;
            });

            return {
                ...state,
                searches,
            };
        },
        [SUBSCRIBE_SAVED_SEARCH_REQUEST]: (state: SavedSearchState, action: ActionWithPayload<any>) => {
            let searches = cloneDeep(state.searches);
            searches.forEach((x) => {
                if (x.savedSearchId === action.payload) {
                    x.sendAlert = true;
                }
            });

            return {
                ...state,
                searches,
            };
        },
        [UNSUBSCRIBE_ALL_SAVED_SEARCH_REQUEST]: (state: SavedSearchState) => {
            let searches = cloneDeep(state.searches);
            searches.forEach((x) => {
                x.sendAlert = false;
            });

            return {
                ...state,
                searches,
            };
        },
        [UNSUBSCRIBE_SAVED_SEARCH_REQUEST]: (state: SavedSearchState, action: ActionWithPayload<any>) => {
            let searches = cloneDeep(state.searches);
            searches.forEach((x) => {
                if (x.savedSearchId === action.payload) {
                    x.sendAlert = false;
                }
            });

            return {
                ...state,
                searches,
            };
        },
    },
    DEFAULT_STATE
);

/* SELECTORS */
const savedSearchSelector = (state: GlobalState) => state.savedSearch;

const idSelector = (state: GlobalState, id) => id;

export const savedSearchSearchesSelector = createSelector(savedSearchSelector, (savedSearch) => savedSearch.searches);

export const hasSavedSearchesSelector = createSelector(
    savedSearchSearchesSelector,
    (savedSearches) => savedSearches.length > 0
);

const getSavedSearchById: (state: any, selector: any) => any = createSelector(
    [savedSearchSearchesSelector, idSelector],
    (savedSearches, id) => savedSearches.find((x) => x.savedSearchId === id)
);

const getLoadTimeForSavedSearches = createSelector(savedSearchSelector, (savedSearch) => savedSearch.searchesLoaded);

export const getSavedSearchsLoaded = createSelector(savedSearchSelector, (savedSearch) =>
    Boolean(savedSearch.searchesLoaded)
);

export const isSavedSearchesLoading = createSelector(savedSearchSelector, (savedSearch) => savedSearch.searchesLoading);

const itemsByIdSelector = createSelector(savedSearchSelector, (state) => state.itemsById);

export const getSavedSearchItemIds = createSelector(savedSearchSelector, (state) => state.itemIds);
export const getLimitedSavedSearchItemIds = createSelector(savedSearchSelector, (state) => state.itemIds.slice(0, 12));

const getLoadTimeForSavedSearchItems = createSelector(savedSearchSelector, (state) => state.itemsLoaded);

export const isSavedSearchItemsLoading = createSelector(savedSearchSelector, (state) => state.itemsLoading);

export const getSavedSearchesRecentFilters = createSelector(
    savedSearchSelector,
    (state) => state.savedSearchesRecentFilters
);

export const getSavedSearchItem = createSelector(
    [itemsByIdSelector, idSelector],
    (itemsById, id) =>
        itemsById[id] || {
            itemId: id,
            publishTs: 0,
        }
);

const shouldFetchSavedSearches = (state) => {
    const searchesLoaded = getLoadTimeForSavedSearches(state);
    const time = Date.now();
    const diff = time - searchesLoaded;
    if (diff < REDUX_STORE_TIME) {
        return false;
    }
    return !isSavedSearchesLoading(state);
};

// sort/ filtering
const savedSearchSortAscending = (a: any = {}, b: any = {}) => {
    const aFacetString = toFacetString(a.facets).toLowerCase();
    const bFacetString = toFacetString(b.facets).toLowerCase();
    if (bFacetString > aFacetString) {
        return -1;
    } else if (aFacetString > bFacetString) {
        return 1;
    }
    return 0;
};

const compareFunctions = {
    LAST_SENT_ASCENDING: (a: any = {}, b: any = {}) => {
        if (!a || !a.lastSent || !a.lastSent) {
            return 1;
        }
        if (!b || !b.lastSent || !b.lastSent) {
            return -1;
        }
        return a.lastSent === b.lastSent
            ? savedSearchSortAscending(a, b)
            : Date.parse(a.lastSent) - Date.parse(b.lastSent);
    },
    LAST_SENT_DESCENDING: (a: any = {}, b: any = {}) => {
        if (!a || !a.lastSent || !a.lastSent) {
            return 1;
        }
        if (!b || !b.lastSent || !b.lastSent) {
            return -1;
        }
        return a.lastSent === b.lastSent
            ? savedSearchSortAscending(a, b)
            : Date.parse(b.lastSent) - Date.parse(a.lastSent);
    },
    RECENT_ITEM_ASCENDING: (a: any = {}, b: any = {}) => {
        if (!a || !a.mostRecent || !a.mostRecent) {
            return 1;
        }
        if (!b || !b.mostRecent || !b.mostRecent) {
            return -1;
        }
        return a.mostRecent === b.mostRecent
            ? savedSearchSortAscending(a, b)
            : Date.parse(a.mostRecent) - Date.parse(b.mostRecent);
    },
    RECENT_ITEM_DESCENDING: (a: any = {}, b: any = {}) => {
        if (!a || !a.mostRecent || !a.mostRecent) {
            return 1;
        }
        if (!b || !b.mostRecent || !b.mostRecent) {
            return -1;
        }
        return a.mostRecent === b.mostRecent
            ? savedSearchSortAscending(a, b)
            : Date.parse(b.mostRecent) - Date.parse(a.mostRecent);
    },
    SEARCH_TERM_ASCENDING: (a: any = {}, b: any = {}) => {
        return savedSearchSortAscending(a, b);
    },
    SEARCH_TERM_DESCENDING: (a: any = {}, b: any = {}) => {
        return savedSearchSortAscending(b, a);
    },
};

const savedSearchSearchFilter = (wrapper, searchString = '') => {
    const searchTokens = searchString.toLowerCase().trim().split(/\s/);
    const matchingTokens = searchTokens.filter((token) => {
        const facetString = toFacetString(wrapper.facets);
        return `${facetString}`.toLowerCase().includes(token);
    });
    const doesMatch = matchingTokens.length === searchTokens.length;
    return doesMatch;
};

export const getFilteredSavedSearches: (state: any, selector: any) => any = (
    state: GlobalState,
    { count, filterNoResults, page, searchTerms, sort }: any = {}
) => {
    const items = savedSearchSearchesSelector(state);
    const loadedItems = items.filter((item) => !isEmpty(item));
    const loadedItemsCount = loadedItems.length;
    const filteredItems = searchTerms
        ? loadedItems.filter((itemWrapper) => savedSearchSearchFilter(itemWrapper, searchTerms))
        : loadedItems;
    const filteredItems2 = filterNoResults
        ? filteredItems.filter((itemWrapper) => itemWrapper.itemCount)
        : filteredItems;
    const sortedItems = sort ? filteredItems2.sort(compareFunctions[sort]) : filteredItems2;
    return {
        filteredCount: filteredItems.length || 0,
        items: pageSlicer(sortedItems, count, page) || [],
        loadedItemsCount,
        totalCount: items.length || 0,
    };
};

export const getSavedSearchesCount = (state: GlobalState) => {
    const items = savedSearchSearchesSelector(state);

    return items.length;
};

export const createSaveSearchKeyBySearchParams = (searchParams: any): string => {
    if (
        searchParams &&
        searchParams.facets &&
        searchParams.facets[0] &&
        searchParams.facets[0].values &&
        searchParams.facets[0].values[0]
    ) {
        let key = `category_id:${searchParams.facets[0].values[0].value}`;
        return key;
    }
    return '';
};

/* ACTION CREATORS */
export const fetchSavedSearchesIfNeeded = (force: boolean = false) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();
    const authToken = getAuthToken(state);
    if ((force || shouldFetchSavedSearches(state)) && authToken) {
        const deployment = getDeployment(state);
        try {
            dispatch({
                type: LOAD_SAVED_SEARCHES_REQUEST,
            });
            const response = await api.fetchSavedSearches({ authToken, deployment });
            dispatch({
                meta: { actionTime: Date.now() },
                payload: response.data,
                type: LOAD_SAVED_SEARCHES_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: LOAD_SAVED_SEARCHES_FAIL,
            });
        }
    }
    return Promise.resolve();
};

export const submitDeleteSavedSearch = (savedSearch: any) => async (dispatch: Function, getState: Function) => {
    const { savedSearchId } = savedSearch;
    const state = getState();
    const authToken = getAuthToken(state);
    const deployment = getDeployment(state);
    const analytics = getUnfollowSearchAnalytics();
    try {
        dispatch({
            payload: savedSearchId,
            type: DELETE_SAVED_SEARCH_REQUEST,
        });
        dispatch({
            payload: createSaveSearchKeyBySearchParams(savedSearch),
            type: DELETE_SAVE_SEARCH_REQUEST,
        });
        const response = await api.postDeleteSavedSearch({ authToken, deployment, savedSearchId });
        dispatch({
            meta: { actionTime: Date.now(), ...analytics, savedSearchId },
            payload: response.payload,
            type: DELETE_SAVED_SEARCH_SUCCESS,
        });
        dispatch({
            payload: createSaveSearchKeyBySearchParams(savedSearch),
            type: DELETE_SAVE_SEARCH_SUCCESS,
        });
        dispatch(fetchSavedSearchesIfNeeded(true));
    } catch (error) {
        dispatch({
            error: true,
            meta: { savedSearchId },
            payload: error,
            type: DELETE_SAVED_SEARCH_FAIL,
        });
    }
};

type SubmitDeleteSavedSearchUrlParams = {
    savedSearchId: any;
    signature: string;
};

// note: shares redux actions with submitDeleteSavedSearch
export const submitDeleteSavedSearchUrl = ({ savedSearchId, signature }: SubmitDeleteSavedSearchUrlParams) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();
    const deployment = getDeployment(state);
    const analytics = getUnfollowSearchAnalytics();
    try {
        dispatch({
            payload: savedSearchId,
            type: DELETE_SAVED_SEARCH_REQUEST,
        });
        dispatch({
            payload: createSaveSearchKeyBySearchParams(savedSearch),
            type: DELETE_SAVE_SEARCH_REQUEST,
        });
        const response = await api.deleteSavedSearchUrl({ deployment, savedSearchId, signature });
        dispatch({
            meta: { actionTime: Date.now(), ...analytics, savedSearchId },
            payload: response.payload,
            type: DELETE_SAVED_SEARCH_SUCCESS,
        });
        dispatch({
            payload: createSaveSearchKeyBySearchParams(savedSearch),
            type: DELETE_SAVE_SEARCH_SUCCESS,
        });
        dispatch(fetchSavedSearchesIfNeeded(true));
    } catch (error) {
        dispatch({
            error: true,
            meta: { savedSearchId },
            payload: error,
            type: DELETE_SAVED_SEARCH_FAIL,
        });
    }
};

export const toggleSavedSearchEmail = (savedSearchId: number) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const authToken = getAuthToken(state);
    const deployment = getDeployment(state);
    const search = getSavedSearchById(state, savedSearchId);
    if (search.sendAlert) {
        dispatch({
            payload: savedSearchId,
            type: UNSUBSCRIBE_SAVED_SEARCH_REQUEST,
        });
        try {
            const response = await api.postUnsubscribeFromSavedSearch({
                authToken,
                deployment,
                savedSearchId,
            });
            dispatch({
                meta: { savedSearchId },
                payload: response.payload,
                type: UNSUBSCRIBE_SAVED_SEARCH_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                meta: { savedSearchId },
                payload: error,
                type: UNSUBSCRIBE_SAVED_SEARCH_FAIL,
            });
        }
    } else {
        dispatch({
            payload: savedSearchId,
            type: SUBSCRIBE_SAVED_SEARCH_REQUEST,
        });
        try {
            const response = await api.postSubscribeToSavedSearch({
                authToken,
                deployment,
                savedSearchId,
            });
            dispatch({
                meta: { savedSearchId },
                payload: response.payload,
                type: SUBSCRIBE_SAVED_SEARCH_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                meta: { savedSearchId },
                payload: error,
                type: SUBSCRIBE_SAVED_SEARCH_FAIL,
            });
        }
    }
};

export const unsubscribeAllSavedSearchEmail = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const authToken = getAuthToken(state);
    const deployment = getDeployment(state);
    dispatch({
        type: UNSUBSCRIBE_ALL_SAVED_SEARCH_REQUEST,
    });
    try {
        const response = await api.postUnsubscribeAllFromSavedSearch({
            authToken,
            deployment,
        });
        dispatch({
            payload: response.payload,
            type: UNSUBSCRIBE_ALL_SAVED_SEARCH_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: UNSUBSCRIBE_ALL_SAVED_SEARCH_FAIL,
        });
    }
};

export const subscribeAllSavedSearchEmail = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const authToken = getAuthToken(state);
    const deployment = getDeployment(state);
    dispatch({
        type: SUBSCRIBE_ALL_SAVED_SEARCH_REQUEST,
    });
    try {
        const response = await api.postSubscribeAllFromSavedSearch({
            authToken,
            deployment,
        });
        dispatch({
            payload: response.payload,
            type: SUBSCRIBE_ALL_SAVED_SEARCH_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: SUBSCRIBE_ALL_SAVED_SEARCH_FAIL,
        });
    }
};

const shouldFetchSavedSearchItems = (state) => {
    if (isSavedSearchItemsLoading(state)) {
        return false;
    }
    const loaded = getLoadTimeForSavedSearchItems(state);
    const time = Date.now();
    const diff = time - loaded;
    return !loaded || diff > REDUX_STORE_TIME;
};

/* ACTION CREATORS */
export const fetchSavedSearchItemsIfNeeded = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    if (shouldFetchSavedSearchItems(state)) {
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const filters = getSavedSearchesRecentFilters(state);
        try {
            dispatch({
                type: LOAD_SAVED_SEARCH_ITEMS_REQUEST,
            });
            const response = await api.fetchSavedSearchItems({
                authToken,
                deployment,
                filter: filters.keyword,
                page: filters.page,
                pageSize: filters.pageSize,
                sort: filters.sort,
            });
            dispatch({
                meta: { actionTime: Date.now() },
                payload: response.payload,
                type: LOAD_SAVED_SEARCH_ITEMS_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: LOAD_SAVED_SEARCH_ITEMS_FAIL,
            });
        }
    }
    return Promise.resolve();
};

export const onSavedSearchRecentFilterChange = (filters: PaginationFilter) => async (dispatch: Function) => {
    try {
        dispatch({
            payload: filters,
            type: SAVED_SEARCH_FILTER_CHANGE,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error.message,
            type: SAVED_SEARCH_FILTER_CHANGE_FAIL,
        });
    }
};
